import { useQuery } from "@tanstack/react-query";
import type { OrganizationSession, UserSession } from "@veritab/api-contracts";
import { createContext, useContext } from "react";

import { getSession } from "../api/auth";
import { authKeys } from "../api/keys";

export interface AuthContext {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: Error | null;
  user?: UserSession | null;
  organization?: OrganizationSession | null;
}

const AuthContext = createContext<AuthContext | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { data, isLoading, error } = useQuery({
    queryKey: authKeys.session(),
    queryFn: getSession,
    staleTime: 60_000, // tweak
    retry: false,
  });

  const isAuthenticated = !!data?.user;

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        error,
        user: data?.user,
        organization: data?.organization,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
